/**
 * Reconnect-to-different-account flow. Story SCH-1.6 (FR8).
 *
 * Two-step state machine in a single SlidePanel:
 *   Step 1 (enter-key)    — paste new API key + Test Connection
 *   Step 2 (confirm-name) — type the discovered new org name to confirm,
 *                           then click Confirm and reconnect
 *
 * Friction-loaded sibling of RotateKeyPanel (Story 1.3). The mechanical
 * difference: rotate-key keeps the same SCH organization (paste-error
 * guard only warns on org change), reconnect REQUIRES a different org
 * and rejects same-org with a redirect to the rotate flow.
 *
 * SECURITY (NFR9):
 * - Input is type="password".
 * - Plaintext is held in component-local React state only.
 * - Cleared synchronously on success and on hard rejections (auth_failed,
 *   same_organization, org_name_mismatch). Kept on transient
 *   connection_test_failed so the user can retry without retyping.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SlidePanel } from '@/components/ui/slide-panel';
import { useToast } from '@/components/ui/use-toast';

import {
  useTestSubContractorHubConnection,
  useReconnectSubContractorHubIntegration,
} from '@/hooks/integrations/useSubContractorHubConnection';
import type {
  ReconnectErrorCode,
  SubContractorHubIntegration,
} from '@/types/integrations/subcontractorhub';

type Step = 'enter-key' | 'confirm-name';

interface ReconnectPanelProps {
  isOpen: boolean;
  onClose: () => void;
  integration: SubContractorHubIntegration;
}

const RECONNECT_ERROR_MESSAGES: Record<ReconnectErrorCode, string> = {
  auth_failed: 'Invalid API key. Existing connection is unchanged.',
  connection_test_failed: 'Could not reach SubContractorHub. Try again in a moment.',
  integration_not_found: 'Integration not found. Refresh the page and try again.',
  same_organization:
    'This API key belongs to the same organization. To rotate keys for the same account, use Configure → Rotate API Key.',
  org_name_mismatch: 'Organization name does not match. Please re-test the connection.',
  internal_error: 'Could not reconnect. Try again or contact support.',
};

export function ReconnectPanel({ isOpen, onClose, integration }: ReconnectPanelProps) {
  const [step, setStep] = useState<Step>('enter-key');
  const [apiKey, setApiKey] = useState('');
  const [discoveredOrgName, setDiscoveredOrgName] = useState<string | null>(null);
  const [confirmName, setConfirmName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { toast } = useToast();
  const testMutation = useTestSubContractorHubConnection();
  const reconnectMutation = useReconnectSubContractorHubIntegration();

  const isTesting = testMutation.isPending;
  const isReconnecting = reconnectMutation.isPending;
  const isAnyPending = isTesting || isReconnecting;

  const resetAll = () => {
    setStep('enter-key');
    setApiKey('');
    setDiscoveredOrgName(null);
    setConfirmName('');
    setErrorMessage(null);
  };

  const handleClose = () => {
    if (isAnyPending) return;
    resetAll();
    onClose();
  };

  const handleTestConnection = async () => {
    setErrorMessage(null);
    try {
      const response = await testMutation.mutateAsync({ apiKey });
      if (response.error) {
        setErrorMessage(
          response.error.code === 'auth_failed'
            ? 'Invalid API key. Please verify and try again.'
            : 'Could not reach SubContractorHub. Try again in a moment.',
        );
        return;
      }
      const newName = response.data.externalOrgName?.trim() ?? '';
      if (!newName) {
        setErrorMessage(
          'Could not resolve the new account\'s organization name. Check the API key and try again.',
        );
        return;
      }
      // Same-org guard on the frontend (the backend re-checks too).
      // SCH-BUG-7a: case-insensitive, mirroring the backend casefold guard.
      if (
        integration.externalOrgName &&
        newName.toLowerCase() === integration.externalOrgName.trim().toLowerCase()
      ) {
        setErrorMessage(RECONNECT_ERROR_MESSAGES.same_organization);
        return;
      }
      setDiscoveredOrgName(newName);
      setConfirmName('');
      setStep('confirm-name');
    } catch {
      setErrorMessage('Could not reach SubContractorHub. Try again in a moment.');
    }
  };

  const handleConfirmReconnect = async () => {
    if (!discoveredOrgName) return;
    setErrorMessage(null);
    try {
      const response = await reconnectMutation.mutateAsync({
        apiKey,
        confirmOrgName: discoveredOrgName,
      });
      if (response.error) {
        const code = response.error.code;
        const message =
          RECONNECT_ERROR_MESSAGES[code] ?? RECONNECT_ERROR_MESSAGES.internal_error;
        // Hard rejections that require re-validating the new key — return
        // user to Step 1 with the input cleared.
        if (
          code === 'same_organization' ||
          code === 'org_name_mismatch' ||
          code === 'auth_failed'
        ) {
          setStep('enter-key');
          setApiKey('');
          setDiscoveredOrgName(null);
          setConfirmName('');
          setErrorMessage(message);
        } else if (code === 'connection_test_failed') {
          // Transient — keep the user where they are so they can retry.
          setErrorMessage(message);
        } else {
          toast({ title: 'Could not reconnect', description: message, variant: 'destructive' });
        }
        return;
      }
      toast({
        title: `Reconnected to ${response.data.externalOrgName}`,
      });
      resetAll();
      onClose();
    } catch {
      toast({
        title: 'Could not reconnect',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleBack = () => {
    setStep('enter-key');
    setDiscoveredOrgName(null);
    setConfirmName('');
    setErrorMessage(null);
    // Keep apiKey so the user doesn't have to repaste.
  };

  const canTest = apiKey.trim().length > 0 && !isAnyPending;
  const canConfirm =
    discoveredOrgName !== null &&
    confirmName === discoveredOrgName &&
    !isAnyPending;
  const showNameMismatchHint =
    discoveredOrgName !== null &&
    confirmName.length > 0 &&
    confirmName !== discoveredOrgName;

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Reconnect to a different SubContractorHub account"
      width="lg"
      topOffset="4.5rem"
    >
      <div className="space-y-5">
        <div className="space-y-2 rounded-md border bg-muted/30 p-4">
          <p className="text-sm">
            <span className="text-muted-foreground">Current organization: </span>
            <span className="font-medium">{integration.externalOrgName ?? '—'}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            Future syncs from a different account will not re-link to or overwrite
            previously imported projects. Mappings for the current organization
            will be cleared.
          </p>
          {/* TODO(Epic 3): show count of imported projects that will become
              orphaned-but-historical references. Source:
              SELECT COUNT(*) FROM projects WHERE tenant_id=$1 AND external_source='subcontractorhub'. */}
        </div>

        {step === 'enter-key' && (
          <>
            <div className="space-y-2">
              <Label htmlFor="reconnect-new-key">New API key</Label>
              <Input
                id="reconnect-new-key"
                type="password"
                autoComplete="off"
                spellCheck={false}
                placeholder="Paste API key for the new account"
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  if (errorMessage) setErrorMessage(null);
                }}
                disabled={isAnyPending}
              />
            </div>

            {errorMessage && (
              <Alert variant="destructive">
                <AlertTitle>Could not test connection</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleClose} disabled={isAnyPending}>
                Cancel
              </Button>
              <Button onClick={handleTestConnection} disabled={!canTest}>
                {isTesting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Test Connection
              </Button>
            </div>
          </>
        )}

        {step === 'confirm-name' && discoveredOrgName !== null && (
          <>
            <div className="space-y-1 rounded-md border bg-muted/30 p-4">
              <p className="text-sm">
                <span className="text-muted-foreground">Connected to: </span>
                <span className="font-medium">{discoveredOrgName}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Replacing: {integration.externalOrgName ?? '—'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reconnect-confirm-name">
                Type <strong>{discoveredOrgName}</strong> to confirm
              </Label>
              <Input
                id="reconnect-confirm-name"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                disabled={isAnyPending}
              />
              {showNameMismatchHint && (
                <p className="text-xs text-muted-foreground">
                  Names do not match. Type the organization name exactly as shown
                  above.
                </p>
              )}
            </div>

            {errorMessage && (
              <Alert variant="destructive">
                <AlertTitle>Could not reconnect</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            <div className="flex items-center justify-between gap-2 pt-2">
              <Button variant="ghost" onClick={handleBack} disabled={isAnyPending}>
                ← Back
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleClose} disabled={isAnyPending}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmReconnect}
                  disabled={!canConfirm}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isReconnecting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Confirm and reconnect
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </SlidePanel>
  );
}
